import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { navy, status } from '../theme/theme';

export type Bar = { key: string; label: string; count: number; muted?: boolean };

/**
 * Einreihiges Balkendiagramm (eine Serie, eine Farbe → keine Legende) mit
 * Hover-Tooltip und begleitender Zahlentabelle (nutzbar ohne Farbe/Screenreader).
 */
export function DistributionChart({ title, bars, total, testId, orientation = 'vertical', tableCaption }: {
  title: string;
  bars: Bar[];
  total?: number;
  testId?: string;
  orientation?: 'vertical' | 'horizontal';
  tableCaption?: string;
}) {
  const max = Math.max(1, ...bars.map((b) => b.count));
  const sum = total ?? bars.reduce((a, b) => a + b.count, 0);
  return (
    <Box data-testid={testId}>
      <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {orientation === 'vertical' ? (
        <Box aria-hidden sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 120, px: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          {bars.map((b) => (
            <Tooltip key={b.key} title={`${b.label}: ${b.count} ${b.count === 1 ? 'Ticket' : 'Tickets'}`} placement="top" arrow>
              <Box sx={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', cursor: 'default' }}>
                <Box
                  sx={{
                    width: '100%',
                    height: `${(b.count / max) * 100}%`,
                    minHeight: b.count > 0 ? 3 : 0,
                    backgroundColor: b.muted ? status.neutral.main : navy[500],
                    borderRadius: '4px 4px 0 0',
                    transition: 'height .2s',
                  }}
                />
              </Box>
            </Tooltip>
          ))}
        </Box>
      ) : (
        <Box aria-hidden sx={{ display: 'grid', gap: '6px' }}>
          {bars.map((b) => (
            <Tooltip key={b.key} title={`${b.label}: ${b.count}`} placement="right" arrow>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 180px) 1fr 44px', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
                  {b.label}
                </Typography>
                <Box sx={{ height: 14, backgroundColor: '#F1F3F6', borderRadius: '4px' }}>
                  <Box sx={{ width: `${(b.count / max) * 100}%`, minWidth: b.count ? 3 : 0, height: '100%', backgroundColor: b.muted ? status.neutral.main : navy[500], borderRadius: '0 4px 4px 0' }} />
                </Box>
                <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {b.count}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
      {orientation === 'vertical' && (
        <Box aria-hidden sx={{ display: 'flex', gap: '2px', px: 0.5, mt: 0.5 }}>
          {bars.map((b) => (
            <Typography key={b.key} variant="caption" sx={{ flex: 1, textAlign: 'center', color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1.2 }}>
              {b.key === 'none' ? 'ohne' : b.label}
            </Typography>
          ))}
        </Box>
      )}
      <Box sx={{ overflowX: 'auto', mt: 1 }}>
        <Table size="small" aria-label={tableCaption ?? `${title} als Tabelle`} sx={{ '& td, & th': { py: 0.25, px: 1, fontSize: '0.78rem' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Klasse</TableCell>
              {bars.map((b) => (
                <TableCell key={b.key} align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {b.label}
                </TableCell>
              ))}
              <TableCell align="right">Summe</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                Tickets
              </TableCell>
              {bars.map((b) => (
                <TableCell key={b.key} align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {b.count}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {sum}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
